<template>
    <div class="consulta-publica">
        <div class="cabecalho">
            <!--BUSCA-->
            <div v-if="mostrarBusca" class="busca">
                <span class="busca-titulo">{{ titulo }}</span>

                <div class="p-inputgroup">
                    <InputText :model-value="termo" :placeholder="placeholder"
                    @update:model-value="$emit('update:termo', $event)"
                    @keyup.enter="$emit('pesquisar')"/>

                    <Button icon="pi pi-search" :disabled="!termo" :loading="loading"
                    type="submit" @click="$emit('pesquisar')"/>
                </div>
            </div>

            <!--RESUMO-->
            <div v-if="temResultado" class="resumo">
                <div v-if="aviso" class="aviso" :class="`aviso-${aviso.tom}`">
                    <i :class="aviso.icone"/>

                    <div>
                        <div class="aviso-titulo">{{ aviso.titulo }}</div>

                        <div v-if="aviso.detalhe" class="aviso-detalhe">
                            {{ aviso.detalhe.rotulo }} <strong>{{ aviso.detalhe.valor }}</strong>
                        </div>
                    </div>
                </div>

                <dl v-if="dados.length" class="dados">
                    <div v-for="dado in dados" :key="dado.rotulo">
                        <dt>{{ dado.rotulo }}</dt>
                        <dd>{{ dado.valor }}</dd>
                    </div>
                </dl>

                <div v-if="referencias.length" class="referencias">
                    <span v-for="referencia in referencias" :key="referencia">{{ referencia }}</span>
                </div>
            </div>
        </div>

        <!--CARTELAS-->
        <ProgressSpinner v-if="loading" class="carregando"/>

        <template v-else>
            <div v-if="errorMessage" class="mensagem">{{ errorMessage }}</div>

            <div v-for="grupo in grupos" :key="grupo.titulo || 'cartelas'">
                <div v-if="grupo.titulo" class="grupo-titulo">{{ grupo.titulo }}</div>

                <ul class="cartelas">
                    <li v-for="cartela in grupo.cartelas" :key="cartela.id" class="cartela"
                    :class="{
                        'cartela-premiada': cartela.winner,
                        'cartela-acumulado': cartela.wonAccumulatedPrize,
                        'cartela-cancelada': cartela.cancelled
                    }">
                        <div class="cartela-topo">
                            <span class="cartela-id">
                                <span class="cartela-rotulo">Cartela</span> {{ cartela.id }}
                            </span>

                            <span v-if="cartela.wonAccumulatedPrize" class="tag tag-acumulado">Acumulado</span>
                            <span v-else-if="cartela.winner" class="tag">Premiada</span>
                            <span v-else-if="cartela.cancelled" class="tag tag-cancelada">Cancelada</span>
                        </div>

                        <div v-if="mostrarEstabelecimento && cartela.establishment" class="cartela-estabelecimento">
                            {{ cartela.establishment }}
                        </div>

                        <div class="ordem-numeros">
                            <div class="numero-cartela" v-for="ball in cartela.ballOfGames" :key="ball.id"
                            :class="{'numero-sorteado': numeroSorteado(ball, cartela)}">
                                {{ ball.number }}
                            </div>
                        </div>

                        <ul v-if="cartela.awards && cartela.awards.length" class="premiacoes">
                            <li v-for="award in cartela.awards" :key="award.type"
                            :class="{'premiacao-acumulado': award.type === 'ACUMULADO'}">
                                <span>{{ award.label }}</span>

                                <span v-if="award.value" class="premiacao-valor">
                                    {{ formatarParaReais(award.value) }}
                                </span>
                            </li>

                            <li v-if="cartela.prizeWon && cartela.awards.length > 1" class="premiacao-total">
                                <span>Total</span>
                                <span class="premiacao-valor">{{ formatarParaReais(cartela.prizeWon) }}</span>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </template>
    </div>
</template>

<script>
import { computed } from 'vue'
import { formatarParaReais } from '../utilities/formato'

/*
  Casca visual das telas públicas de consulta (bilhete compartilhado e ganhadores por sorteio).
  Concentra o layout para que as duas telas exibam as cartelas do mesmo jeito.
*/

export default {

    props: {
        titulo: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        mostrarBusca: { type: Boolean, default: false },
        termo: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        errorMessage: { type: String, default: null },

        // { tom: 'destaque' | 'acumulado' | 'neutro', icone, titulo, detalhe: { rotulo, valor } }
        aviso: { type: Object, default: null },

        // [{ rotulo, valor }]
        dados: { type: Array, default: () => [] },
        referencias: { type: Array, default: () => [] },

        // [{ titulo, cartelas }]
        grupos: { type: Array, default: () => [] },

        // Rodadas do sorteio; a ultimaRodada indica até onde as bolas foram sorteadas
        rounds: { type: Object, default: () => ({}) },

        mostrarEstabelecimento: { type: Boolean, default: false },
    },

    emits: ['update:termo', 'pesquisar'],

    setup(props){

        const temResultado = computed(() => {
            return !props.loading && props.grupos.some(grupo => grupo.cartelas.length > 0)
        })

        /*
          Uma cartela premiada deve ser mostrada como estava na rodada em que recebeu seu último prêmio,
          sem incluir as bolas que saíram depois. Cartelas sem prêmio não têm números destacados.
          O fallback para ultimaRodada preserva a exibição caso um payload antigo não traga a rodada no prêmio.
        */
        function rodadaLimite(cartela){
            if (!cartela.winner) {
                return null
            }

            const rodadasPremiadas = (cartela.awards || [])
                .map(award => Number(award.round))
                .filter(round => Number.isFinite(round) && round > 0)

            if (rodadasPremiadas.length) {
                return Math.max(...rodadasPremiadas)
            }

            return Number(props.rounds.ultimaRodada)
        }

        function numeroSorteado(ball, cartela){
            const limite = rodadaLimite(cartela)
            const rodadaDaBola = Number(ball.roundWasDrawn)

            return Number.isFinite(limite)
                && limite > 0
                && Number.isFinite(rodadaDaBola)
                && rodadaDaBola > 0
                && rodadaDaBola <= limite
        }

        return {
            temResultado,
            numeroSorteado,
            formatarParaReais,
        }
    }
}
</script>

<style scoped>
.consulta-publica {
    --texto: #33383b;
    --suave: #6f767b;
    --linha: #e2e6e8;
    --fundo: #f5f6f7;
    --destaque: #4f7d5c;
    --destaque-fundo: #f1f5f2;
    --sorteado-fundo: #cae5d1;
    --acumulado: #97763f;
    --acumulado-fundo: #f8f4ec;

    color: var(--texto);
    font-size: 1rem;
    padding-bottom: 24px;
}

/* CABEÇALHO */
.cabecalho {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 16px 12px;
    background: var(--fundo);
    border-bottom: 1px solid var(--linha);
}

.busca {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.busca-titulo {
    font-size: 1.5rem;
    font-weight: 600;
}

.busca .p-inputgroup {
    width: 100%;
    max-width: 360px;
}

.resumo {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 640px;
}

/* AVISO */
.aviso {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #ffff;
    border: 1px solid var(--linha);
    border-left: 3px solid var(--destaque);
    border-radius: 8px;
}

.aviso i {
    font-size: 1.5rem;
    color: var(--destaque);
}

.aviso-titulo {
    font-size: 1.125rem;
    font-weight: 600;
}

.aviso-detalhe {
    font-size: 1rem;
    color: var(--suave);
}

.aviso-acumulado {
    border-left-color: var(--acumulado);
    background: var(--acumulado-fundo);
}

.aviso-acumulado i {
    color: var(--acumulado);
}

.aviso-neutro {
    border-left-color: #c3c9cd;
}

.aviso-neutro i {
    color: var(--suave);
}

/* DADOS */
.dados {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px 24px;
    margin: 0;
    padding: 14px 16px;
    background: #ffff;
    border: 1px solid var(--linha);
    border-radius: 8px;
}

.dados dt {
    font-size: 1rem;
    color: var(--suave);
}

.dados dd {
    margin: 2px 0 0;
    font-size: 1.125rem;
    font-weight: 600;
}

.referencias {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 20px;
    font-size: 1rem;
    color: var(--suave);
}

/* LISTA DE CARTELAS */
.grupo-titulo {
    max-width: 1100px;
    margin: 22px auto 0;
    padding: 0 12px;
    font-size: 1.125rem;
    font-weight: 600;
}

.cartelas {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 270px), 1fr));
    gap: 12px;
    max-width: 1100px;
    margin: 0 auto;
    padding: 12px;
    list-style: none;
}

.cartela {
    padding: 14px;
    background: #ffff;
    border: 1px solid var(--linha);
    border-radius: 10px;
}

.cartela-topo {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.cartela-id {
    font-size: 1.25rem;
    font-weight: 700;
}

.cartela-rotulo {
    font-size: 1rem;
    font-weight: 400;
    color: var(--suave);
}

.cartela-estabelecimento {
    margin-top: 2px;
    color: var(--suave);
}

.tag {
    padding: 3px 10px;
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid #d8e4dc;
    border-radius: 999px;
    background: var(--destaque-fundo);
    color: var(--destaque);
}

.tag-acumulado {
    border-color: #e8dcc6;
    background: var(--acumulado-fundo);
    color: var(--acumulado);
}

.tag-cancelada {
    border-color: var(--linha);
    background: #f3f4f5;
    color: var(--suave);
}

.cartela-premiada {
    border-color: var(--destaque);
}

.cartela-acumulado {
    border-color: var(--acumulado);
}

.cartela-cancelada {
    opacity: 0.65;
}

/* NÚMEROS */
.ordem-numeros {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 5px;
    margin-top: 12px;
}

.numero-cartela {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
    font-size: clamp(1.125rem, 4.4vw, 1.5rem);
    color: var(--texto);
    border: 1px solid #eceff0;
    border-radius: 6px;
}

.numero-sorteado {
    font-weight: 600;
    background: var(--sorteado-fundo);
    border-color: var(--sorteado-fundo);
}

/* PREMIAÇÕES DA CARTELA */
.premiacoes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 12px 0 0;
    padding: 12px 0 0;
    border-top: 1px solid var(--linha);
    list-style: none;
}

.premiacoes li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 1rem;
}

.premiacao-valor {
    font-weight: 600;
    white-space: nowrap;
}

.premiacao-acumulado .premiacao-valor {
    color: var(--acumulado);
}

.premiacao-total {
    margin-top: 2px;
    padding-top: 8px;
    border-top: 1px dashed var(--linha);
    color: var(--suave);
}

.premiacao-total .premiacao-valor {
    color: var(--texto);
}

/* ESTADOS */
.carregando {
    display: flex;
    margin: 48px auto 0;
}

.mensagem {
    margin: 32px 12px;
    text-align: center;
    font-size: 1.25rem;
    color: var(--suave);
}

@media screen and (min-width: 768px) {
    .cabecalho {
        padding: 20px;
    }

    .cartelas {
        gap: 14px;
    }
}
</style>
